import React from "react";
import {
  Package,
  User,
  Building,
  Hash,
  CheckCircle2,
  XCircle,
  Edit,
} from "lucide-react";
import { Modal } from "@/components/ui";
import { DetailField } from "./table_view";
import { fmt } from "@/lib/formatters";

export const ViewModal = ({ viewingItem, setViewingItem, activeTab, handleEdit }) => (
  <Modal
    isOpen={!!viewingItem}
    onClose={() => setViewingItem(null)}
    title={`View ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Details`}
  >
    {viewingItem && (
      <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
        {/* Header Identity Section */}
        <div className="flex items-center gap-4 pb-4 border-b border-slate-100 mb-2">
          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center text-brand-blue border border-slate-100 shrink-0">
            {activeTab === "item" ? (
              <Package size={24} />
            ) : activeTab === "supplier" ? (
              <User size={24} />
            ) : activeTab === "stores" ? (
              <Building size={24} />
            ) : (
              <Hash size={24} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-brand-dark truncate">
                {viewingItem.itemName ||
                  viewingItem.name ||
                  viewingItem.title}
              </h3>
              <span className="text-[10px] font-bold bg-blue-50 text-brand-blue px-2 py-0.5 rounded border border-blue-100 shrink-0 uppercase">
                {viewingItem.itemCode || viewingItem.code || "REF-ID-0"}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-3">
              {viewingItem.active !== false ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{" "}
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                  <div className="w-1.5 h-1.5 rounded-full bg-slate-300" />{" "}
                  Inactive
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Detail Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {activeTab === "item" ? (
            <>
              <DetailField
                label="Item Type"
                value={viewingItem.itemType || viewingItem.type || "—"}
                className="capitalize"
              />
              <DetailField
                label="Unit"
                value={
                  viewingItem.units?.unitName ||
                  viewingItem.units?.title ||
                  "N/A"
                }
              />
              <DetailField
                label="Category"
                value={
                  viewingItem.category?.categoryName ||
                  viewingItem.category?.title ||
                  "N/A"
                }
              />
              <DetailField
                label="Brand"
                value={
                  viewingItem.brand?.brandName ||
                  viewingItem.brand?.title ||
                  "N/A"
                }
              />
              <DetailField
                label="Item Name"
                value={viewingItem.itemName}
                fullWidth
              />
              <DetailField label="Item Code" value={viewingItem.itemCode} />
              <DetailField label="MOQ" value={viewingItem.moq} />
              <DetailField label="Fixing Type" value={viewingItem.fixingType} />
              <DetailField label="Quality" value={viewingItem.quality} />
              <DetailField label="Colour" value={viewingItem.colour} />
              <DetailField label="Profile" value={viewingItem.profile} />
              <DetailField label="Height" value={viewingItem.height} />
              <DetailField label="Width" value={viewingItem.width} />
              <DetailField label="Thickness" value={viewingItem.thickness} />
              <DetailField
                label="HSN/SAC Code"
                value={viewingItem.hsnSacCode}
              />
              <DetailField label="Barcode" value={viewingItem.barcode} />
              <div className="sm:col-span-2 grid grid-cols-2 gap-3 pt-1">
                <div className="flex items-center justify-between transition-all duration-300">
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider opacity-70 mb-0.5">
                      Unique Item
                    </div>
                    <div className="text-xs font-semibold text-brand-dark">
                      {viewingItem.isUnique ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                  {viewingItem.isUnique ? (
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  ) : (
                    <XCircle size={14} className="text-slate-300" />
                  )}
                </div>
                <div className="flex items-center justify-between transition-all duration-300">
                  <div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider opacity-70 mb-0.5">
                      Batch/Expiry
                    </div>
                    <div className="text-xs font-semibold text-brand-dark">
                      {viewingItem.isBatchExpiry ? "Enabled" : "Disabled"}
                    </div>
                  </div>
                  {viewingItem.isBatchExpiry ? (
                    <CheckCircle2 size={14} className="text-emerald-500" />
                  ) : (
                    <XCircle size={14} className="text-slate-300" />
                  )}
                </div>
              </div>
              {viewingItem.isUnique && viewingItem.uniqueName && (
                <DetailField
                  label="Unique Identifier"
                  value={viewingItem.uniqueName}
                  fullWidth
                />
              )}
              <DetailField
                label="Description"
                value={viewingItem.itemDescription}
                fullWidth
              />
              <DetailField
                label="Created On"
                value={
                  (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                    ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                    : "N/A"
                }
                fullWidth
              />
            </>
          ) : activeTab === "supplier" ? (
            <>
              <DetailField
                label="Company Name"
                value={viewingItem.name}
                fullWidth
              />
              <DetailField
                label="Mobile Number"
                value={viewingItem.mobile}
              />
              <DetailField
                label="Email Address"
                value={viewingItem.email}
              />
              <DetailField
                label="GST Registration"
                value={
                  viewingItem.gstRegisteredType?.title ||
                  viewingItem.gstNo ||
                  "N/A"
                }
              />
              <DetailField label="GST Number" value={viewingItem.gstNo} />
              <DetailField
                label="Address"
                value={`${viewingItem.address}, ${viewingItem.district}, ${viewingItem.state}`}
                fullWidth
              />
              <DetailField
                label="Created On"
                value={
                  (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                    ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                    : "N/A"
                }
                fullWidth
              />
              <div className="sm:col-span-2 pt-2">
                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">
                  Bank Information
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <DetailField
                    label="Bank Name"
                    value={viewingItem.bankName}
                  />
                  <DetailField
                    label="IFSC Code"
                    value={viewingItem.bankIfsc}
                  />
                  <DetailField
                    label="Account Holder"
                    value={viewingItem.bankAccountHolder}
                  />
                  <DetailField
                    label="Account Number"
                    value={viewingItem.bankAccountNo}
                  />
                </div>
              </div>
            </>
          ) : activeTab === "stores" ? (
            <>
              <DetailField
                label="Store Name"
                value={viewingItem.title}
                fullWidth
              />
              <DetailField label="Store Code" value={viewingItem.code} />
              <DetailField
                label="Contact Number"
                value={viewingItem.mobile || "N/A"}
              />
              <DetailField
                label="Full Address"
                value={viewingItem.address}
                fullWidth
              />
              <DetailField
                label="Created On"
                value={
                  (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                    ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                    : "N/A"
                }
                fullWidth
              />
            </>
          ) : activeTab === "purchase" ? (
            <>
              <DetailField label="Bill No" value={viewingItem.billNo} />
              <DetailField
                label="Vendor"
                value={
                  typeof viewingItem.vendor === "object"
                    ? viewingItem.vendor?.name
                    : viewingItem.vendor
                }
              />
              <DetailField
                label="Bill Date"
                value={
                  viewingItem.date
                    ? new Date(viewingItem.date).toLocaleDateString()
                    : "N/A"
                }
              />
              <DetailField
                label="Net Payable"
                value={fmt(viewingItem.netPayable || 0)}
                className="text-emerald-600"
              />
              <DetailField label="Buyer" value={viewingItem.buyer} />
              <DetailField
                label="Store"
                value={
                  typeof viewingItem.store === "object"
                    ? viewingItem.store?.title
                    : viewingItem.store
                }
              />

              <div className="sm:col-span-2 pt-4">
                <h4 className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-3 border-b border-slate-100 pb-1">
                  Purchased Items
                </h4>
                <div className="space-y-2">
                  {viewingItem.items?.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs"
                    >
                      <div className="flex-1">
                        <span className="font-bold text-brand-dark">
                          {typeof item.item === "object"
                            ? item.item?.itemName || item.item?.name
                            : "Item"}
                        </span>
                        <span className="ml-2 text-slate-400">
                          × {item.qty} {item.unit}
                        </span>
                      </div>
                      <div className="font-bold text-slate-700">
                        {fmt(item.amount + (item.taxAmount || 0))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <DetailField
                label="Notes"
                value={viewingItem.remarks}
                fullWidth
              />
              <DetailField
                label="Created On"
                value={
                  (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                    ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                    : "N/A"
                }
                fullWidth
              />
            </>
          ) : (
            <>
              <DetailField
                label="Name / Title"
                value={viewingItem.title || viewingItem.name || viewingItem.registeredTypeName}
                fullWidth
              />
              <DetailField
                label="Description"
                value={viewingItem.description}
                fullWidth
              />
              <DetailField
                label="Created On"
                value={
                  (viewingItem.createdAt || viewingItem.createdDate || viewingItem.created)
                    ? new Date(viewingItem.createdAt || viewingItem.createdDate || viewingItem.created).toLocaleString()
                    : "N/A"
                }
                fullWidth
              />
            </>
          )}
        </div>

        <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
          {/* <button
            onClick={() => {
              setViewingItem(null);
              handleEdit(viewingItem);
            }}
            className="btn-primary px-8 py-2 font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:scale-[1.02] active:scale-95 transition-all shadow-md shadow-blue-500/10"
          >
            <Edit size={12} /> Edit Details
          </button> */}
          <button
            onClick={() => setViewingItem(null)}
            className="btn-secondary px-8 py-2 font-black text-[10px] uppercase tracking-widest hover:bg-slate-100 transition-all border-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    )}
  </Modal>
);
